import { type ReactElement, useId } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Box, DialogLayout, Inline, Panel, Stack, Text } from '@/ui';
import { Button, TextField } from '@/ui/primitives';
import type { UserProfileFormState } from '@/features/users/hooks/useUserProfileForm';

type UserProfileFormProps = { fieldDirection: 'row' | 'column'; profile: UserProfileFormState };

export const UserProfileForm = ({
  fieldDirection,
  profile,
}: UserProfileFormProps): ReactElement => {
  const firstNameId = useId();
  const lastNameId = useId();
  const emailId = useId();

  return (
    <Panel
      title="Profile"
      actions={
        <Button
          variant="contained"
          disableElevation
          fullWidthOnMobile={false}
          disabled={profile.saveDisabled}
          aria-label="Save profile"
          onClick={profile.onSave}
        >
          Update
        </Button>
      }
    >
      <Stack gap="md">
        <Stack direction={fieldDirection} gap="md">
          <Box sx={{ flex: 1 }}>
            <TextField
              id={firstNameId}
              label="First Name"
              value={profile.firstName}
              onChange={(event) => {
                profile.onFirstNameChange(event.target.value);
              }}
              fullWidth
              disabled={profile.isLoading}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <TextField
              id={lastNameId}
              label="Last Name"
              value={profile.lastName}
              onChange={(event) => {
                profile.onLastNameChange(event.target.value);
              }}
              fullWidth
              disabled={profile.isLoading}
            />
          </Box>
        </Stack>
        <Box>
          <TextField
            id={emailId}
            label="Email"
            type="email"
            value={profile.email}
            onChange={(event) => {
              profile.onEmailChange(event.target.value);
            }}
            fullWidth
            disabled={profile.isLoading || profile.emailReadOnly}
            error={profile.emailError}
            helperText={profile.emailHelperText}
          />
        </Box>
        {!profile.emailVerifiedAt ? (
          <Inline gap="md" align="center" wrap="wrap">
            <Button
              variant="outlined"
              disableElevation
              fullWidthOnMobile={false}
              disabled={profile.resendVerificationDisabled}
              onClick={profile.onResendVerification}
            >
              Resend Verification Email
            </Button>
          </Inline>
        ) : null}
      </Stack>
      <DialogLayout
        open={profile.verificationDialogOpen}
        onClose={profile.onVerificationCancel}
        maxWidth="xs"
      >
        <Stack gap="lg">
          <Stack gap="sm" alignItems="center">
            <Text role="sectionTitle" sx={{ textAlign: 'center' }}>
              Verify Email
            </Text>
            <Text role="body" color="textSecondary" sx={{ textAlign: 'center' }}>
              Enter the verification code sent to {profile.verificationTargetEmail}.
            </Text>
          </Stack>
          {profile.verificationError ? (
            <Text role="captionSmall" color="error" sx={{ textAlign: 'center' }}>
              {profile.verificationError}
            </Text>
          ) : null}
          <Stack gap="md">
            <TextField
              label="Verification Code"
              placeholder="Enter verification code"
              value={profile.verificationCode}
              onChange={(event) => {
                profile.onVerificationCodeChange(event.target.value);
              }}
              fullWidth
              disabled={profile.verificationSubmitting || profile.verificationSendPending}
            />
            <Button
              variant="primary"
              onClick={profile.onVerificationSubmit}
              disabled={
                profile.verificationCode.trim().length === 0 ||
                profile.verificationSubmitting ||
                profile.verificationSendPending
              }
              loading={profile.verificationSubmitting}
            >
              Verify
            </Button>
          </Stack>
          <Text role="body" color="textSecondary" sx={{ textAlign: 'center' }}>
            Didn&apos;t receive a code?{' '}
            {profile.verificationResendOnCooldown ? (
              <Box component="span" sx={{ color: 'text.secondary' }}>
                Resend in {profile.verificationResendSecondsRemaining}s
              </Box>
            ) : (
              <Box
                component="button"
                type="button"
                onClick={profile.onVerificationResendCode}
                disabled={profile.verificationSubmitting || profile.verificationSendPending}
                sx={{
                  border: 'none',
                  background: 'transparent',
                  color: 'primary.light',
                  cursor: 'pointer',
                  font: 'inherit',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Resend
              </Box>
            )}
          </Text>
        </Stack>
      </DialogLayout>
      <ConfirmDialog
        open={profile.reverificationDialogOpen}
        title="Verify Your Session"
        description="Enter your current password to continue updating your email."
        confirmLabel="Continue"
        confirmColor="primary"
        confirmDisabled={profile.reverificationPassword.trim().length === 0}
        loading={profile.reverificationSubmitting}
        onClose={profile.onReverificationCancel}
        onConfirm={profile.onReverificationSubmit}
      >
        <Stack gap="md">
          <TextField
            label="Current Password"
            type="password"
            value={profile.reverificationPassword}
            onChange={(event) => {
              profile.onReverificationPasswordChange(event.target.value);
            }}
            fullWidth
            disabled={profile.reverificationSubmitting}
          />
          {profile.reverificationError ? (
            <Text role="captionSmall" color="error">
              {profile.reverificationError}
            </Text>
          ) : null}
        </Stack>
      </ConfirmDialog>
    </Panel>
  );
};
